<template>
  <BasePageCard>
    <div class="itineraries-view">
      <div class="itineraries-view__header">
        <h2 class="itineraries-view__title">Roteiros</h2>

        <div v-if="!showEmptyState" class="itineraries-view__header-actions">
          <a-button
            class="itineraries-view__filters-btn"
            :class="{ 'itineraries-view__filters-btn--active': hasModalFilter }"
            @click="filterModalVisible = true"
          >
            <a-icon type="control" /> Filtros
          </a-button>

          <a-button
            v-if="canCreate"
            class="itineraries-view__upload-btn"
            :loading="uploading"
            :disabled="creating || uploading"
            @click="onBulkUpload"
          >
            <a-icon type="upload" /> Upload em massa
          </a-button>

          <a-button
            v-if="canCreate"
            type="primary"
            class="itineraries-view__create-btn"
            :loading="creating"
            :disabled="uploading"
            @click="openDrawer"
          >
            <a-icon v-if="!creating" type="plus" /> Novo roteiro
          </a-button>
        </div>
      </div>

      <ItinerariesFilters
        v-if="!showEmptyState"
        :counts="counts"
        :active-status="activeFilters.status"
        @filter-change="onFilterChange"
      />

      <BaseBulkActionsBar
        v-if="!showEmptyState"
        :selected-count="selectedRowKeys.length"
        :total-count="items.length"
        :actions="bulkActions"
        @toggle-all="onToggleAll"
        @action="onBulkAction"
      />

      <ItinerariesEmptyState
        v-if="showEmptyState"
        :can-create="canCreate"
        :uploading="uploading"
        @create-itinerary="openDrawer"
        @bulk-upload="onBulkUpload"
      />

      <a-empty
        v-else-if="showNoResultsState"
        description="Nenhum roteiro encontrado para esse filtro."
        class="itineraries-view__no-results"
      />

      <ItinerariesTable
        v-else
        :items="items"
        :loading="loading"
        :selected-row-keys="selectedRowKeys"
        :can-change-status="canChangeStatus"
        :status-updating-ids="statusUpdatingIds"
        :pagination="pagination"
        @selection-change="selectedRowKeys = $event"
        @toggle-status="onToggleStatus"
        @view-details="onViewDetails"
        @table-change="onTableChange"
      />

      <ItinerariesFilterModal
        :visible="filterModalVisible"
        :filters="activeFilters"
        :suppliers="selectedSuppliers"
        @apply="onApplyFilters"
        @clear="onClearFilters"
        @close="filterModalVisible = false"
      />

      <ItinerariesCreateDrawer
        :visible="drawerVisible"
        :draft="activeDraft"
        :mode="drawerMode"
        @close="closeDrawer"
        @created="onCreated"
        @saved="onStopsSaved"
      />

      <ItinerariesDetailsDrawer
        :visible="detailsVisible"
        :itinerary="detailsItinerary"
        :can-update="canUpdate"
        :can-change-status="canChangeStatus"
        @close="closeDetails"
        @edit-stops="onEditStops"
        @changed="onDetailsChanged"
      />
    </div>
  </BasePageCard>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import BasePageCard from "@/shared/components/BasePageCard.vue";
import ItinerariesFilters from "@/modules/Itineraries/components/List/ItinerariesFilters.vue";
import ItinerariesTable from "@/modules/Itineraries/components/List/ItinerariesTable.vue";
import ItinerariesFilterModal from "@/modules/Itineraries/components/List/ItinerariesFilterModal.vue";
import ItinerariesEmptyState from "@/modules/Itineraries/components/List/ItinerariesEmptyState.vue";
import BaseBulkActionsBar from "@/shared/components/BaseBulkActionsBar.vue";
import ItinerariesCreateDrawer from "@/modules/Itineraries/components/Create/ItinerariesCreateDrawer.vue";
import ItinerariesDetailsDrawer from "@/modules/Itineraries/components/Details/ItinerariesDetailsDrawer.vue";
import { handleApiError } from "@/modules/Itineraries/utils/handleApiError";
import { downloadBlob } from "@/shared/utils/downloadBlob";
import { ITINERARY_STATUS } from "../constants/itineraryStatus";

const PENDING_DRAFT_KEY = "itineraries.pendingDraftId";

export default {
  name: "ItinerariesIndexView",
  components: {
    BasePageCard,
    ItinerariesFilters,
    ItinerariesTable,
    ItinerariesFilterModal,
    ItinerariesEmptyState,
    BaseBulkActionsBar,
    ItinerariesCreateDrawer,
    ItinerariesDetailsDrawer,
  },
  data() {
    return {
      selectedRowKeys: [],
      statusUpdatingIds: [],
      hasFetched: false,
      filterModalVisible: false,
      selectedSuppliers: [],
      activeFilters: { search: "", status: "todos", sort: null },
      bulkActionPending: null,
      creating: false,
      uploading: false,
      drawerVisible: false,
      drawerMode: "create",
      activeDraft: null,
      detailsVisible: false,
      detailsItinerary: null,
    };
  },
  computed: {
    ...mapGetters("itineraries", ["items", "loading", "counts", "pagination"]),
    hasActiveFilter() {
      return (
        !!this.activeFilters.search ||
        this.activeFilters.status !== "todos" ||
        this.selectedSuppliers.length > 0
      );
    },
    hasModalFilter() {
      return (
        this.activeFilters.status !== "todos" ||
        this.activeFilters.sort !== null ||
        this.selectedSuppliers.length > 0
      );
    },
    showEmptyState() {
      return (
        this.hasFetched &&
        !this.loading &&
        this.pagination.total === 0 &&
        !this.hasActiveFilter
      );
    },
    showNoResultsState() {
      return (
        this.hasFetched &&
        !this.loading &&
        this.pagination.total === 0 &&
        this.hasActiveFilter
      );
    },
    selectedItineraryIds() {
      return this.items
        .filter((item) => this.selectedRowKeys.includes(item.id))
        .map((item) => item.itineraryId);
    },
    canCreate() {
      return (
        this.getPermissionFw("itinerary.store") &&
        this.getPermissionFw("itinerary.update")
      );
    },
    canChangeStatus() {
      return this.getPermissionFw("itinerary.status");
    },
    bulkActions() {
      const actions = [];

      if (this.canChangeStatus) {
        actions.push({
          key: "enable",
          label: "Habilitar selecionados",
          icon: "check-circle",
          variant: "success",
          loading: this.bulkActionPending === "enable",
        });
        actions.push({
          key: "disable",
          label: "Desabilitar selecionados",
          icon: "minus-circle",
          variant: "danger",
          loading: this.bulkActionPending === "disable",
        });
      }

      actions.push({
        key: "export",
        label: "Exportar selecionados",
        icon: "file-text",
        variant: "neutral",
        loading: this.bulkActionPending === "export",
      });

      return actions;
    },
    canUpdate() {
      return this.getPermissionFw("itinerary.update");
    },
  },
  async created() {
    await this.cleanupPendingDraft();
    this.fetchRoteiros({ page: 1 }).finally(() => {
      this.hasFetched = true;
    });
  },
  beforeDestroy() {
    this.resetState();
  },
  methods: {
    ...mapActions("itineraries", [
      "fetchRoteiros",
      "updateItinerariesStatus",
      "resetState",
      "createDraft",
      "deleteItinerary",
      "importItineraries",
      "exportItineraries",
    ]),
    onFilterChange(filters) {
      this.activeFilters = { ...this.activeFilters, ...filters };
      this.selectedRowKeys = [];
      this.fetchRoteiros({
        search: filters.search,
        status: filters.status,
        page: 1,
      });
    },
    onApplyFilters({ sort, status, suppliers }) {
      this.selectedSuppliers = suppliers;
      this.activeFilters = { ...this.activeFilters, sort, status };
      this.selectedRowKeys = [];
      this.filterModalVisible = false;
      this.fetchRoteiros({
        personIds: suppliers.map((s) => Number(s.key)),
        status,
        sort,
        page: 1,
      });
    },
    onClearFilters() {
      this.selectedSuppliers = [];
      this.activeFilters = { ...this.activeFilters, sort: null, status: "todos" };
      this.selectedRowKeys = [];
      this.filterModalVisible = false;
      this.fetchRoteiros({ personIds: [], status: "todos", sort: null, page: 1 });
    },
    onTableChange({ page, perPage, sort }) {
      this.selectedRowKeys = [];
      const sizeChanged = perPage !== this.pagination.perPage;
      const sortChanged = sort !== this.activeFilters.sort;
      this.activeFilters = { ...this.activeFilters, sort };
      this.fetchRoteiros({
        page: sizeChanged || sortChanged ? 1 : page,
        perPage,
        sort,
      });
    },
    onToggleAll(checked) {
      this.selectedRowKeys = checked ? this.items.map((item) => item.id) : [];
    },
    onBulkAction(key) {
      if (key === "enable") {
        this.onBulkEnable();
        return;
      }

      if (key === "disable") {
        this.onBulkDisable();
        return;
      }

      if (key === "export") {
        this.onBulkExport();
      }
    },
    async onBulkExport() {
      this.bulkActionPending = "export";
      try {
        const { blob, filename } = await this.exportItineraries({
          itineraryIds: this.selectedItineraryIds,
        });
        downloadBlob(blob, filename);
      } catch (error) {
        handleApiError(this, error, "Não foi possível exportar os roteiros");
      } finally {
        this.bulkActionPending = null;
      }
    },
    async onBulkEnable() {
      this.bulkActionPending = "enable";
      try {
        await this.updateItinerariesStatus({
          itineraryIds: this.selectedItineraryIds,
          status: ITINERARY_STATUS.ATIVO,
        });
        this.selectedRowKeys = [];
      } catch (error) {
        handleApiError(this, error, "Não foi possível habilitar os roteiros");
      } finally {
        this.bulkActionPending = null;
      }
    },
    async onBulkDisable() {
      this.bulkActionPending = "disable";
      try {
        await this.updateItinerariesStatus({
          itineraryIds: this.selectedItineraryIds,
          status: ITINERARY_STATUS.INATIVO,
        });
        this.selectedRowKeys = [];
      } catch (error) {
        handleApiError(this, error, "Não foi possível desabilitar os roteiros");
      } finally {
        this.bulkActionPending = null;
      }
    },
    async onToggleStatus({ record, status }) {
      if (this.statusUpdatingIds.includes(record.itineraryId)) return;
      this.statusUpdatingIds = [...this.statusUpdatingIds, record.itineraryId];
      try {
        await this.updateItinerariesStatus({
          itineraryIds: [record.itineraryId],
          status,
        });
      } catch (error) {
        handleApiError(this, error, "Não foi possível alterar o status do roteiro");
      } finally {
        this.statusUpdatingIds = this.statusUpdatingIds.filter(
          (id) => id !== record.itineraryId
        );
      }
    },
    async openDrawer() {
      if (this.creating) return;
      this.creating = true;
      try {
        this.activeDraft = await this.createDraft();
        sessionStorage.setItem(PENDING_DRAFT_KEY, String(this.activeDraft.id));
        this.drawerMode = "create";
        this.drawerVisible = true;
      } catch (error) {
        handleApiError(this, error, "Não foi possível criar o roteiro");
      } finally {
        this.creating = false;
      }
    },
    closeDrawer() {
      this.drawerVisible = false;
      this.drawerMode = "create";
      this.activeDraft = null;
      sessionStorage.removeItem(PENDING_DRAFT_KEY);
    },
    cleanupPendingDraft() {
      const pendingId = sessionStorage.getItem(PENDING_DRAFT_KEY);
      if (!pendingId) return Promise.resolve();
      sessionStorage.removeItem(PENDING_DRAFT_KEY);
      return this.deleteItinerary({ itineraryId: Number(pendingId) }).catch(
        () => {}
      );
    },
    onViewDetails(record) {
      this.detailsItinerary = { ...record, stops: [...(record.stops || [])] };
      this.detailsVisible = true;
    },
    closeDetails() {
      this.detailsVisible = false;
      this.detailsItinerary = null;
    },
    onDetailsChanged() {
      this.fetchRoteiros({ page: this.pagination.current });
    },
    onEditStops(itinerary) {
      this.detailsVisible = false;
      this.detailsItinerary = null;
      this.activeDraft = {
        id: itinerary.itineraryId,
        codigo: itinerary.id,
        nome: itinerary.nome,
        descricao: itinerary.descricao,
        paradas: (itinerary.stops || []).map((stop) => ({ ...stop })),
        status: "draft",
        step: 1,
      };
      this.drawerMode = "edit";
      this.drawerVisible = true;
    },
    async onStopsSaved() {
      this.closeDrawer();
      await this.fetchRoteiros({ page: this.pagination.current });
    },
    async onCreated() {
      this.closeDrawer();
      this.$notification.success({
        message: "Roteiro criado com sucesso",
        description:
          "Vincule-o à tabela de frete para calcular o valor da viagem",
        class: "itinerary-success-notification",
        icon: (h) =>
          h("a-icon", {
            props: { type: "check-circle", theme: "filled" },
          }),
      });
      await this.fetchRoteiros({ page: 1, refreshCounts: true });
    },
    onBulkUpload() {
      if (this.uploading) return;
      const input = document.createElement("input");
      input.type = "file";
      input.accept =
        ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      input.onchange = (event) => this.onBulkUploadSelected(event);
      input.click();
    },
    async onBulkUploadSelected(event) {
      const input = event && event.target ? event.target : null;
      const file = input && input.files ? input.files[0] : null;

      if (input) {
        input.value = "";
      }

      if (!file) return;

      const hasValidExtension = /\.xlsx$/i.test(file.name || "");
      if (!hasValidExtension) {
        this.$message.warning("Selecione um arquivo .xlsx para importar");
        return;
      }

      this.uploading = true;
      try {
        const result = await this.importItineraries({ file });
        this.$notification.success({
          message: "Importação iniciada com sucesso",
          description:
            (result && result.message) ||
            "Seu arquivo foi enviado e os roteiros serão atualizados.",
        });
        await this.fetchRoteiros({ page: 1, refreshCounts: true });
      } catch (error) {
        handleApiError(
          this,
          error,
          "Não foi possível importar o arquivo de roteiros",
        );
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.itineraries-view {
  padding: 24px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 20px;
    font-weight: 600;
    color: @color-text-dark;
    margin: 0;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__upload-btn {
    height: 36px;
    border-radius: 8px;
    border-color: #d9d9d9;
    background: #ffffff;
    color: rgba(0, 0, 0, 0.88);

    &:hover,
    &:focus {
      border-color: #bfbfbf;
      background: #ffffff;
      color: rgba(0, 0, 0, 0.88);
    }
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__filters-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    border-radius: 6px;
    color: @color-text-medium;

    &--active {
      color: @color-primary;
      border-color: @color-primary;
    }
  }

  &__create-btn {
    background-color: @color-primary;
    border-color: @color-primary;
    border-radius: 6px;
    height: 36px;

    &:hover,
    &:focus {
      background-color: @color-primary-hover;
      border-color: @color-primary-hover;
    }
  }

  &__no-results {
    padding: 48px;
  }
}
</style>

<style lang="less">
@import "~@/shared/styles/variables.less";

.itinerary-success-notification.ant-notification-notice {
  border-radius: 8px;
  padding: 16px 24px;

  .ant-notification-notice-message {
    font-size: 16px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
    margin-bottom: 4px;
  }

  .ant-notification-notice-description {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.45);
  }

  .ant-notification-notice-icon {
    color: @color-success-light;
    font-size: 21px;
  }
}
</style>
